import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import {Checkbox} from "@/components/ui/checkbox";
import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {Textarea} from "@/components/ui/textarea";
import {cn, getTermsLegalPath} from "@/lib/utils";
import { Trash2 } from "lucide-react";
import {NavLink} from "react-router-dom";

const DynamicFormField = ({question, form, disabled, view, showRequiredMark = true}) => {
    const {t, i18n} = useTranslation();
    // `showRequiredMark={false}` trata la pregunta como opcional: no se pinta el asterisco
    // y no se propaga `required` al campo (usado por la edición de comunicaciones en el panel admin).
    const isRequired = showRequiredMark ? question.required : false;
    const filesRef = form.register("files");
    const [selectedFiles, setSelectedFiles] = useState([]);

    useEffect(() => {
        if (question?.type !== "file") {
            return;
        }

        const subscription = form.watch((value, {name}) => {
            if (!name || (name !== "files" && name !== question.alias)) {
                return;
            }

            const rawValue = name === "files" ? value?.files : value?.[question.alias];
            const filesArray =
                rawValue && typeof rawValue.length === "number"
                    ? Array.from(rawValue)
                    : [];

            console.log("[DynamicField][watch] Field value changed", {
                questionId: question.id,
                fieldName: name,
                fileCount: filesArray.length,
                fileNames: filesArray.map((file) => file?.name),
                valueType: rawValue ? rawValue.constructor?.name : typeof rawValue,
            });

            if (name === question.alias) {
                setSelectedFiles(filesArray.map((file) => file.name));
            }
        });

        return () => subscription.unsubscribe();
    }, [form, question.alias, question.id, question.type]);

    const findLanguageEntry = (entries) =>
        Array.isArray(entries) // Verifica que `entries` sea un array
            ? entries.find((e) => e.language === i18n.language) // Busca la entrada en el idioma seleccionado
            : null; // Retorna null si `entries` no es un array

    const getLocalizedText = (field) => findLanguageEntry(question[field])?.text;

    const text = question.label || getLocalizedText("texts") || question.alias;
    const placeholder = getLocalizedText("placeholder") || "";
    const description = getLocalizedText("description") || "";

    const getOptionText = (option) =>
        Array.isArray(option?.text)
            ? option.text.find((t) => t.language === i18n.language)?.text || ""
            : "";

    const options =
        question.options?.map((option) => ({
            value: option.value,
            text: getOptionText(option),
        })) ?? [];

    const mergeFiles = (existingFiles = [], newFiles = []) => {
        const merged = [];
        const seen = new Set();

        [...existingFiles, ...newFiles].forEach((file) => {
            if (!(file instanceof File)) return;
            const uniqueKey = `${file.name}:${file.size}:${file.lastModified}`;
            if (seen.has(uniqueKey)) return;
            seen.add(uniqueKey);
            merged.push(file);
        });

        return merged;
    };

    switch (question.type) {
        case "password":
            return (
                <div
                    className="flex flex-col md:flex-row w-full gap-4 md:gap-6"
                    data-type="password"
                >
                    <FormField
                        key={question.id}
                        control={form.control}
                        name={question.alias}
                        disabled={disabled}
                        required={isRequired}
                        render={({field}) => (
                            <FormItem className="w-fit">
                                <FormLabel>
                                    {text}
                                    {isRequired && (
                                        <span className="text-red-500"> *</span>
                                    )}
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        {...field}
                                        type={view === "summary" ? "text" : "password"}
                                        placeholder={placeholder}
                                    />
                                </FormControl>
                                <FormDescription>{description}</FormDescription>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />
                </div>
            );
        case "textarea":
            return (
                <FormField
                    key={question.id}
                    control={form.control}
                    name={question.alias}
                    disabled={disabled}
                    required={isRequired}
                    render={({field}) => (
                        <FormItem className="w-full" data-type="textarea">
                            <FormLabel>
                                {text}
                                {isRequired && <span className="text-red-500"> *</span>}
                            </FormLabel>
                            <FormControl>
                                <Textarea {...field} placeholder={placeholder}/>
                            </FormControl>
                            <FormDescription>{description}</FormDescription>
                            <FormMessage/>
                        </FormItem>
                    )}
                />
            );
        case "date":
            return (
                <FormField
                    key={question.id}
                    control={form.control}
                    name={question.alias}
                    disabled={disabled}
                    required={isRequired}
                    render={({field}) => (
                        <FormItem className="w-fit" data-type="date">
                            <FormLabel>
                                {text}
                                {isRequired && <span className="text-red-500"> *</span>}
                            </FormLabel>
                            <FormControl>
                                <Input
                                    {...field}
                                    type="date"
                                    placeholder={placeholder}
                                    value={field.value || ""}
                                />
                            </FormControl>
                            <FormDescription>{description}</FormDescription>
                            <FormMessage/>
                        </FormItem>
                    )}
                />
            );
        case "terms":
            return (
                <FormField
                    key={question.id}
                    control={form.control}
                    name={question.alias}
                    disabled={disabled}
                    required={isRequired}
                    render={({field}) => (
                        <FormItem
                            className="flex flex-col md:flex-row gap-3 md:items-center w-full space-y-0"
                            data-type="terms"
                        >
                            <FormControl>
                                <Checkbox
                                    {...field}
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                />
                            </FormControl>
                            <FormLabel>
                                <NavLink
                                    to={getTermsLegalPath(question)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="terms"
                                >
                                    {text}
                                    {isRequired && (
                                        <span className="text-red-500"> *</span>
                                    )}
                                </NavLink>
                            </FormLabel>
                        </FormItem>
                    )}
                />
            );
        case "file":
            return (
                <FormField
                    key={question.id}
                    control={form.control}
                    name={question.alias}
                    disabled={disabled}
                    required={isRequired}
                    render={({field}) => (
                        <FormItem className="w-full" data-type="file">
                            <FormLabel className="text-base font-semibold">
                                {text}
                                {isRequired && <span className="text-red-500"> *</span>}
                            </FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Input
                                        {...filesRef}
                                        type="file"
                                        multiple={true}
                                        placeholder={placeholder}
                                        className="hidden"
                                        id={`file-input-${question.id}`}
                                        onChange={(e) => {
                                            const fileList = e.target?.files;
                                            const fileArray = fileList ? Array.from(fileList) : [];
                                            const currentFieldFiles =
                                                field.value && typeof field.value.length === "number"
                                                    ? Array.from(field.value).filter((item) => item instanceof File)
                                                    : [];
                                            console.log("[DynamicField][FileInput] Change event triggered", {
                                                questionId: question.id,
                                                fileCount: fileList?.length ?? 0,
                                                fileNames: fileArray.map((file) => file.name),
                                            });
                                            if (fileArray.length > 0) {
                                                const mergedFiles = mergeFiles(currentFieldFiles, fileArray);
                                                // Actualizar el campo específico con todos los archivos seleccionados/acumulados
                                                field.onChange(mergedFiles);
                                                // También actualizar el campo global files para compatibilidad
                                                form.setValue("files", mergedFiles);
                                            }
                                            if (fileArray.length === 0) {
                                                console.log("[DynamicField][FileInput] No files selected after change", {
                                                    questionId: question.id,
                                                });
                                            }
                                            const toFileNames = (value) =>
                                                value && typeof value.length === "number"
                                                    ? Array.from(value).map((file) => file?.name)
                                                    : [];
                                            const globalFiles = form.getValues("files");
                                            const aliasValue = form.getValues(question.alias);
                                            console.log("[DynamicField][FileInput] Form values after update", {
                                                questionId: question.id,
                                                globalFileCount: globalFiles?.length ?? 0,
                                                globalFileNames: toFileNames(globalFiles),
                                                aliasFileCount: aliasValue?.length ?? 0,
                                                aliasFileNames: toFileNames(aliasValue),
                                            });
                                        }}
                                    />
                                    <label
                                        htmlFor={`file-input-${question.id}`}
                                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-primary/50 rounded-lg cursor-pointer bg-primary/5 hover:bg-primary/10 transition-colors duration-200"
                                    >
                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            <svg 
                                                className="w-10 h-10 mb-3 text-primary" 
                                                fill="none" 
                                                stroke="currentColor" 
                                                viewBox="0 0 24 24" 
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round" 
                                                    strokeWidth={2} 
                                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                                                />
                                            </svg>
                                            <p className="mb-2 text-sm text-gray-600">
                                                <span className="font-semibold text-primary">{t("forms.fileUpload.clickToUpload")}</span> {t("forms.fileUpload.files")}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {placeholder || "PDF, DOC, DOCX, JPG, PNG"}
                                            </p>
                                        </div>
                                    </label>
                                    {selectedFiles.length > 0 && (
                                        <div className="mt-3 p-4 bg-gray-100 rounded-lg border border-primary/30">
                                            <p className="text-base font-semibold text-primary mb-2">
                                                {selectedFiles.length} {selectedFiles.length === 1 ? t("forms.fileUpload.files").slice(0, -1) : t("forms.fileUpload.files")} {t("forms.fileUpload.clickToUpload").toLowerCase()}
                                            </p>
                                            <ul className="space-y-2">
                                                {selectedFiles.map((fileName, index) => (
                                                    <li key={`${fileName}-${index}`} className="flex items-center justify-between gap-3 text-base text-gray-800 font-medium">
                                                        <span className="flex items-center">
                                                            <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                            {fileName}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            data-testid="remove-selected-file"
                                                            data-file-name={fileName}
                                                            aria-label={`${t("buttonTexts.delete")} ${fileName}`}
                                                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-red-500 hover:bg-red-100 transition-colors"
                                                            onClick={() => {
                                                                const currentFiles =
                                                                    field.value && typeof field.value.length === "number"
                                                                        ? Array.from(field.value).filter((item) => item instanceof File)
                                                                        : [];
                                                                const nextFiles = currentFiles.filter((_, fileIndex) => fileIndex !== index);
                                                                field.onChange(nextFiles);
                                                                form.setValue("files", nextFiles);
                                                                setSelectedFiles(nextFiles.map((file) => file.name));
                                                            }}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </FormControl>
                            <FormDescription className="text-sm text-gray-600 mt-2">
                                {description || t("forms.fileUpload.multipleFiles")}
                            </FormDescription>
                            <FormDescription className="text-xs text-gray-500 mt-1" data-testid="file-upload-max-size">
                                {t("forms.fileUpload.maxSize")}
                            </FormDescription>
                            <FormMessage/>
                        </FormItem>
                    )}
                />
            );
        case "select":
            return (
                <FormField
                    key={question.id}
                    control={form.control}
                    name={question.alias}
                    required={isRequired}
                    render={({field}) => (
                        <FormItem className="w-fit" data-type="select"  data-question-id={question.id}>
                            <FormLabel>
                                {text}
                                {isRequired && <span className="text-red-500"> *</span>}
                            </FormLabel>
                            <Select
                                onValueChange={field.onChange}
                                value={field.value || question.defaultValue || ""}
                            >
                                <FormControl>
                                    <SelectTrigger disabled={disabled}>
                                        <SelectValue placeholder={placeholder}/>
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {options.map((option, index) => (
                                        <SelectItem key={`option-${index}` } data-option-id={option.id} value={option.value}>
                                            {option.text}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormDescription>{description}</FormDescription>
                            <FormMessage/>
                        </FormItem>
                    )}
                />
            );
        case "simpleSelect":
            return (
                <FormField
                    key={question.id}
                    control={form.control}
                    name={question.alias}
                    required={isRequired}
                    render={({field}) => (
                        <FormItem className="w-fit" data-type="simple-select" data-question-id={question.id}>
                            <FormLabel>
                                {text}
                                {isRequired && <span className="text-red-500"> *</span>}
                            </FormLabel>
                            <Select
                                onValueChange={field.onChange}
                                value={field.value || question.defaultValue || ""}
                            >
                                <FormControl>
                                    <SelectTrigger disabled={disabled}>
                                        <SelectValue placeholder={placeholder}/>
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {question.options.map((option, index) => (
                                        <SelectItem key={`simple-option-${index}`} value={option.value} data-option-id={option.id}>
                                            {option.text}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormDescription>{description}</FormDescription>
                            <FormMessage/>
                        </FormItem>
                    )}
                />
            );

        case "radio":
            return (
                <FormField
                    key={question.id}
                    control={form.control}
                    name={question.alias}
                    required={isRequired}
                    render={({field}) => (
                        <FormItem className="space-y-4 w-full" data-type="radio" data-question-id={question.id}>
                            <FormLabel>
                                {text}
                                {isRequired && <span className="text-red-500"> *</span>}
                            </FormLabel>
                            <FormControl>
                                <RadioGroup
                                    disabled={disabled}
                                    onValueChange={field.onChange}
                                    defaultValue={field.value}
                                    className={cn(
                                        "flex space-x-4",
                                        options.length > 2 && "flex-col space-x-0 space-y-3",
                                    )}
                                >
                                    {options.map((option, index) => (
                                        <FormItem
                                            key={`option-${index}`}
                                            className="flex items-center space-x-2 space-y-0"
                                        >
                                            <FormControl>
                                                <RadioGroupItem value={option.value} data-option-id={option.id}/>
                                            </FormControl>
                                            <FormLabel className="normal-case">
                                                {option.text}
                                            </FormLabel>
                                        </FormItem>
                                    ))}
                                </RadioGroup>
                            </FormControl>
                            <FormMessage/>
                        </FormItem>
                    )}
                />
            );
        default:
            return (
                <FormField
                    key={question.id}
                    control={form.control}
                    name={question.alias}
                    disabled={disabled}
                    required={isRequired}
                    render={({field}) => (
                        <FormItem className="w-fit" data-type="default">
                            <FormLabel>
                                {text}
                                {isRequired && <span className="text-red-500"> *</span>}
                            </FormLabel>
                            <FormControl>
                                <Input {...field} placeholder={placeholder} type="text"/>
                            </FormControl>
                            <FormDescription>{description}</FormDescription>
                            <FormMessage/>
                        </FormItem>
                    )}
                />
            );
    }
};

export {DynamicFormField};
